import { Color, Spacing } from '@persona-id/design-tokens';
import { Fragment } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import { isConditionalValueEmpty } from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor/utils';
import {
  ConditionalRule,
  ConditionalRuleValueConfig,
  getConditionalRuleParts,
  getOperatorDisplayName,
  LogicBlockV2,
  RuleValueTypes,
} from 'dashboard/lib/workflows';
import { paragraphMixins } from 'design-system/mixins/text';
import { assertNever } from 'lib/utils/types';

const RuleContainer = styled.div`
  background: ${Color.CoreSlate50};
  padding: 18px 22px;
  display: grid;
  grid-template-columns: 60px auto;
  grid-column-gap: ${Spacing[2]};
  margin-bottom: ${Spacing[2]};
`;

const BaseRuleBlock = styled.div`
  margin-top: ${Spacing[1]};
`;

const Pill = styled.span`
  background: ${Color.CoreSlate200};
  display: inline-block;
  padding: 2px 6px;
  border-radius: ${BORDER_RADIUSES.rounded};
  margin-left: 3px;
  margin-right: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;

  ${paragraphMixins.sm};
`;

const TextBlock = styled.p`
  padding-top: 6px;
  padding-bottom: ${Spacing[1]};
  min-width: 100px;
  font-size: 13px;
  line-height: ${Spacing[4]};
`;

interface LogicBlockProps {
  andRules: LogicBlockV2;
  prefixText: string;
  keyPrefix: string;
  orIndex: number;
  validateRule: (rule: ConditionalRule) => boolean;
}

const getRightHandDisplay = (ruleValueConfig: ConditionalRuleValueConfig) => {
  const { type, value, paths } = ruleValueConfig;

  switch (type) {
    case RuleValueTypes.Value: {
      if (Array.isArray(value)) {
        if (value.length > 2) {
          return `${value[0]}, ${value[1]}, and ${value.length - 2} more`;
        } else {
          return value.join(', ');
        }
      }
      return JSON.stringify(value);
    }
    case RuleValueTypes.Field: {
      return paths[0];
    }
    default: {
      return assertNever(type);
    }
  }
};

const LogicBlock = ({
  andRules,
  prefixText,
  keyPrefix,
  orIndex,
  validateRule,
}: LogicBlockProps) => {
  return (
    <RuleContainer data-test='logic-block__rule-container'>
      {andRules.and.map((baseRule, andIndex) => {
        const { operator, valueLeftConfig, valueRightConfig } = getConditionalRuleParts(baseRule);
        const isRuleValid = validateRule(baseRule);

        // For every rule, start with the prefixText, and for every following rule,
        // pre-pend with "And"
        // For prefixText="Show if"
        // Show if     Rule
        // And         Rule
        // And         Rule
        const displayText = andIndex === 0 ? prefixText : 'And';
        return (
          <Fragment key={`${keyPrefix}_view_${orIndex}_0_LogicBlock`}>
            <TextBlock>{displayText}</TextBlock>
            {isRuleValid && (
              <BaseRuleBlock>
                <Pill>{valueLeftConfig.value.var}</Pill>
                <Pill>{getOperatorDisplayName(operator)}</Pill>
                {!isConditionalValueEmpty(valueRightConfig?.value) && (
                  <Pill>{getRightHandDisplay(valueRightConfig!)}</Pill>
                )}
              </BaseRuleBlock>
            )}
            {!isRuleValid && (
              <BaseRuleBlock>
                <Pill>Empty rule</Pill>
              </BaseRuleBlock>
            )}
          </Fragment>
        );
      })}
    </RuleContainer>
  );
};

export default LogicBlock;
